<template>
  <v-card
    :color="color"
    elevation="4"
    class="role-kpi-card"
  >
    <v-card-text class="pb-2">
      <div class="d-flex align-center justify-space-between">
        <div class="flex-grow-1">
          <h3 class="text-h6 font-weight-medium text-white mb-1">{{ title }}</h3>
          <div class="d-flex align-center">
            <span class="text-h4 font-weight-bold text-white mr-2">{{ displayValue }}</span>
            <v-chip
              v-if="trend"
              :color="trendColor"
              size="small"
              class="text-white"
            >
              <v-icon :icon="trendIcon" size="small" class="mr-1"></v-icon>
              {{ trend.percentage }}%
            </v-chip>
          </div>
          <p class="text-caption text-white opacity-80 mb-0" v-if="subtitle">{{ subtitle }}</p>
        </div>
        <div class="kpi-icon-container">
          <v-icon :icon="icon" size="48" class="text-white opacity-60"></v-icon>
        </div>
      </div>
    </v-card-text>
    <!-- P13-V7B (refutation): the optional action button is RETIRED. No consumer ever passed one and
         nothing listened to its `action` event; its only other input was the permission mask below. -->
  </v-card>
</template>

<script setup lang="ts">
import { computed } from 'vue'

interface Props {
  title: string
  value: number | string
  icon: string
  color?: string
  trend?: {
    direction: 'up' | 'down' | 'stable'
    percentage: number
  }
  subtitle?: string
}

/**
 * ── P13-V5A-R6 · A CARD DOES NOT INVENT A GATE ITS CALLER NEVER ASKED FOR ──
 *
 * `permission` defaulted to `'read.basic'`. `hasPermission` below then masks the VALUE of any card
 * whose permission the actor lacks (`return '---'`), and `'read.basic'` is held by exactly one
 * role — `super_admin`, via its `'*'` wildcard. Every other role's permission list
 * (`useRolePermissions.js`) omits it, so EVERY KPI card that did not name a permission explicitly
 * was masked to `---` for every non-global actor, on every load.
 *
 * That is how `field_supervisor` read "Agencias Gestionadas ---" in a real browser while
 * `GET /api/analytics/zone-kpis` was answering 200 with the number for its authorised partition:
 * a client-side gate erasing a fact the server had already authorised, scoped and released to that
 * actor. The `route_operator` cards escaped only because they happen to name permissions their own
 * role holds.
 *
 * The default is removed, not widened: a card that names no permission is ungated (the branch
 * `if (!props.permission) return true` was unreachable while a default existed), and a card that
 * DOES name one keeps its gate exactly as before.
 *
 * ── P13-V7B (refutation) · A CARD HAS NO GATE AT ALL ──
 *
 * The gate R6 kept for cards that DID name a permission was still a client-side authority: it read
 * the per-role permission table of `useRolePermissions.js` (a parallel map keyed through legacy
 * aliases) and replaced the value with `---`. A value reaches this card only after its producer
 * admitted the SESSION's role, so there is nothing left for a client list to decide. The `permission`
 * prop, the mask and the table are gone: the card shows the value its home passes.
 */
const props = withDefaults(defineProps<Props>(), {
  color: 'primary'
})

const displayValue = computed(() => {
  if (typeof props.value === 'number') {
    if (props.value >= 1000000) {
      return `${(props.value / 1000000).toFixed(1)}M`
    } else if (props.value >= 1000) {
      return `${(props.value / 1000).toFixed(1)}K`
    }
    return props.value.toLocaleString()
  }
  
  return props.value
})

const trendColor = computed(() => {
  if (!props.trend) return 'grey'
  switch (props.trend.direction) {
    case 'up': return 'success'
    case 'down': return 'error'
    case 'stable': return 'warning'
    default: return 'grey'
  }
})

const trendIcon = computed(() => {
  if (!props.trend) return 'mdi-minus'
  switch (props.trend.direction) {
    case 'up': return 'mdi-trending-up'
    case 'down': return 'mdi-trending-down'
    case 'stable': return 'mdi-trending-neutral'
    default: return 'mdi-minus'
  }
})
</script>

<style scoped>
.role-kpi-card {
  transition: all 0.3s ease;
  cursor: pointer;
  height: 140px;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.role-kpi-card:hover {
  transform: translateY(-2px);
}

.kpi-icon-container {
  flex-shrink: 0;
}

.opacity-80 {
  opacity: 0.8;
}

.opacity-60 {
  opacity: 0.6;
}
</style>
